import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronDown, ChevronRight, Trash2, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import { AdminListPage } from '../components/AdminListPage'
import { useAdminSubmissions } from '../hooks/useAdminSubmissions'
import type { ApplicationSubmissionDocument } from '@/types'

const STATUS_VARIANTS: Record<
  ApplicationSubmissionDocument['status'],
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending_payment: 'outline',
  pending: 'secondary',
  reviewing: 'outline',
  accepted: 'default',
  waitlisted: 'secondary',
  rejected: 'destructive',
}

export function AdminApplicationSubmissionsPage() {
  const { id: applicationId } = useParams<{ id: string }>()
  const { submissions, loading, error, updateStatus, deleteSubmission } = useAdminSubmissions(
    applicationId ?? ''
  )
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  async function handleDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget)
    try {
      await deleteSubmission(deleteTarget)
    } finally {
      setDeletingId(null)
      setDeleteTarget(null)
      setDeleteOpen(false)
    }
  }

  function formatValue(value: unknown): string {
    if (value === null || value === undefined) return '—'
    if (Array.isArray(value)) return value.join(', ')
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    return String(value)
  }

  function formatDate(ts: unknown): string {
    if (!ts) return '—'
    const date =
      typeof (ts as { toDate?: () => Date }).toDate === 'function'
        ? (ts as { toDate: () => Date }).toDate()
        : new Date(ts as string)
    return date.toLocaleString()
  }

  return (
    <AdminListPage
      title="Submissions"
      action={
        <Button asChild variant="outline">
          <Link to="/admin/applications">← Back to Applications</Link>
        </Button>
      }
      error={error}
      loading={loading}
      skeletonRows={5}
      dialog={
        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogTrigger className="hidden" />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Submission</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to permanently delete this submission? This cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteOpen(false); setDeleteTarget(null) }}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDelete()}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Submitted</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {submissions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                No submissions yet.
              </TableCell>
            </TableRow>
          ) : (
            submissions.map((sub) => (
              <>
                <TableRow
                  key={sub.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
                >
                  <TableCell>
                    {expandedId === sub.id ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {sub.applicantInfo.firstName} {sub.applicantInfo.lastName}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{sub.applicantInfo.email}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(sub.submittedAt)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={sub.status}
                      onValueChange={(v) =>
                        void updateStatus(sub.id, v as ApplicationSubmissionDocument['status'])
                      }
                    >
                      <SelectTrigger className="h-7 w-32 text-xs" onClick={(e) => e.stopPropagation()}>
                        <Badge variant={STATUS_VARIANTS[sub.status]} className="text-xs">
                          {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="reviewing">Reviewing</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      disabled={deletingId === sub.id}
                      onClick={() => { setDeleteTarget(sub.id); setDeleteOpen(true) }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>

                {/* Expanded answers */}
                {expandedId === sub.id && (
                  <TableRow key={`${sub.id}-detail`}>
                    <TableCell colSpan={6} className="bg-muted/30 p-4 whitespace-normal">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {Object.entries(sub.answers).map(([fieldId, answer]) => {
                            const a = answer as { label: string; value: unknown }
                            return (
                              <div key={fieldId} className="min-w-0 space-y-0.5">
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                  {a.label}
                                </p>
                                <p className="whitespace-pre-wrap break-words text-sm">
                                  {formatValue(a.value)}
                                </p>
                              </div>
                            )
                          })}
                        </div>

                        {/* File attachments */}
                        {Object.entries(sub.fileUrls ?? {}).length > 0 && (
                          <div>
                            <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              Attachments
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(sub.fileUrls).flatMap(([, urls]) =>
                                (urls as string[]).map((url: string, i: number) => (
                                  <a
                                    key={`${url}-${i}`}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    View file
                                  </a>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))
          )}
        </TableBody>
      </Table>
    </AdminListPage>
  )
}
